<script>
import SelectedItemsDisplay from '@modules/global/components/selected-items-display/selected-items-display.vue'
import { reduceArraToObjectList } from '@utils/Array/reduceArrayToOBJectList/reduceArrayToOBJectList'

export default {
  name: 'SelectGroupedSearch',
  components: { SelectedItemsDisplay },
  props: {
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Pesquisar',
    },
    selectionPlaceholder: {
      type: String,
      default: 'Todos',
    },
    maxWidth: {
      type: [String, Number],
      default: null,
    },
    menuMaxWidth: {
      type: [String, Number],
      default: 375,
    },
    menuMinWidth: {
      type: [String, Number],
      default: null,
    },
    items: {
      type: [Array, Object],
      default: () => [],
    },
    keyItem: {
      type: String,
      default: 'value',
    },
    groupedBy: {
      type: String,
      default: '',
    },
    groupDisabledText: {
      type: String,
      default: '(sem itens)',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    showDetails: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
    selectableLeafGroups: {
      type: Boolean,
      default: false,
    },
    preserveOrder: {
      type: Boolean,
      default: false,
    },
    disabledItems: {
      type: Array,
      default: () => [],
    },
    hideClearSelection: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      show: false,
      search: '',
      selectedGroups: [],
      selectedItems: [],
      openedGroups: [],
      hasBeenBlurred: false,
    }
  },
  computed: {
    listItems() {
      return this.buildListItems({ applySearch: true })
    },
    fullListItems() {
      return this.buildListItems({ applySearch: false })
    },
    isSearchDisabled() {
      return this.loading || this.disabled || this.isEmptySourceList
    },
    isEmptySourceList() {
      if (Array.isArray(this.items)) return this.items.length === 0
      if (this.items && typeof this.items === 'object')
        return Object.keys(this.items).length === 0
      return true
    },
    isEmptyList() {
      return this.listItems.length === 0
    },
    groupList() {
      return this.listItems.map((groupData) => ({
        id: this.getGroupId({ groupData }),
        value: this.getGroupName(groupData),
        hasItem: groupData.hasItems,
        items: Array.isArray(groupData.items) ? groupData.items : [],
      }))
    },
    selectedItemsValues() {
      return this.getSelectedValuesById(this.items, this.selectedItems)
    },
    showClearSelectedItems() {
      return (
        !this.hideClearSelection &&
        !this.loading &&
        !this.disabled &&
        this.selectedItems.length > 0
      )
    },
    normalizedMaxWidth() {
      return this.normalizeSize(this.maxWidth)
    },
    normalizedMenuMaxWidth() {
      return this.normalizeSize(this.menuMaxWidth)
    },
    validationValue() {
      return this.selectedItemsValues
    },
    errorMessages() {
      if (!this.hasBeenBlurred) return []
      return this.computeErrors({
        value: this.validationValue,
        rules: this.rules,
      })
    },
    computedMenuMinWidth() {
      if (this.menuMinWidth !== null && this.menuMinWidth !== undefined) {
        return this.normalizeSize(this.menuMinWidth)
      }
      void this.show
      const el = this.$refs.activator
      const w = el && el.offsetWidth ? el.offsetWidth : 0
      return w ? w + 'px' : 'auto'
    },
    hasFooter() {
      return !!this.$slots.footer || !!this.$scopedSlots.footer
    },
  },
  watch: {
    value: {
      handler(newVal) {
        this.applyValue(newVal, { skipIfSame: true })
      },
    },
    items: {
      handler() {
        this.pruneSelectedItems()
        this.syncSelectedGroups()
        this.syncOpenedGroups()
      },
    },
    selectedItems: {
      handler() {
        this.setSelectedItems()
        this.syncSelectedGroups()
        if (this.hasBeenBlurred && this.$refs.input) this.$refs.input.validate()
      },
      deep: true,
    },
    show(newValue) {
      if (newValue) {
        if (this.loading || this.disabled) return
        setTimeout(() => {
          if (this.$refs.searchField) this.$refs.searchField.focus()
        }, 300)
      } else {
        if (this.loading || this.disabled) return
        this.hasBeenBlurred = true
        this.$nextTick(() => {
          if (this.$refs.input) this.$refs.input.validate()
        })
      }
    },
  },
  created() {
    this.applyValue(this.value)
  },
  methods: {
    buildListItems({ applySearch }) {
      let data = this.normalizeSourceToArray({ sourceItems: this.items })
      if (applySearch) data = this.applySearchFilter({ data })
      data = this.applyGrouping({ data })
      return this.sortGroups(data)
    },
    normalizeSourceToArray({ sourceItems }) {
      if (Array.isArray(sourceItems)) return [...sourceItems]
      if (sourceItems && typeof sourceItems === 'object')
        return this.convertObjectToArray(sourceItems)
      return []
    },
    applySearchFilter({ data }) {
      if (!this.search || !this.search.length) return data
      return this.filterItems(data, this.search)
    },
    applyGrouping({ data }) {
      if (this.groupedBy && this.groupedBy.length)
        return reduceArraToObjectList(data, this.groupedBy)
      return this.groupItems(data)
    },
    getGroupId({ groupData }) {
      return String(groupData && groupData.name ? groupData.name : '')
    },
    computeErrors({ value, rules }) {
      if (!Array.isArray(rules) || rules.length === 0) return []
      return rules
        .map((rule) => this.applyRule({ rule, value }))
        .filter((msg) => typeof msg === 'string' && msg.length > 0)
    },
    applyRule({ rule, value }) {
      if (typeof rule === 'function') return this.applyRuleFn({ rule, value })
      if (typeof rule === 'string' && rule) return rule
      return null
    },
    applyRuleFn({ rule, value }) {
      const res = rule(value)
      if (res === true || res === undefined) return null
      return typeof res === 'string' ? res : 'Inválido'
    },
    normalizeSize(v) {
      if (typeof v === 'number') return v + 'px'
      if (typeof v === 'string') {
        const t = v.trim()
        if (/^\d+$/.test(t)) return t + 'px'
        return t
      }
      return ''
    },
    clear() {
      this.selectedGroups = []
      this.selectedItems = []
      this.openedGroups = []
    },
    applyValue(val, { skipIfSame = false } = {}) {
      const next = Array.isArray(val) ? val.map(String) : []
      if (skipIfSame && this.isSameSelection(next)) return
      this.selectedItems = next
      this.syncSelectedGroups()
    },
    isSameSelection(next) {
      const current = this.selectedItems.map(String)
      if (next.length !== current.length) return false
      const currentSet = new Set(current)
      return next.every((id) => currentSet.has(id))
    },
    isLeafGroupValue(groupValue) {
      if (!groupValue || typeof groupValue !== 'object') return true
      return Object.keys(groupValue).length === 0
    },
    sortGroups(items) {
      const entries = Object.entries(items).map(([key, value]) => {
        const orderedItems = this.preserveOrder
          ? value
          : value.sort((a, b) => {
              const av = (a.value || '').toString()
              const bv = (b.value || '').toString()
              return av.localeCompare(bv)
            })
        return {
          name: key || '',
          items: orderedItems,
          hasItems: !!(value[0] && value[0].id),
        }
      })
      if (this.preserveOrder) return entries
      return entries.sort((a, b) => {
        if (a.hasItems === b.hasItems) return a.name.localeCompare(b.name)
        return b.hasItems - a.hasItems
      })
    },
    getGroupName(item) {
      if (item.hasItems || this.selectableLeafGroups) return item.name
      return `${item.name} ${this.groupDisabledText}`
    },
    toggleGroupSelection({ groupId, inputValue }) {
      const itemsInGroup = this.getFullGroupItems({ groupId })
      const isChecked = this.isGroupChecked({ groupId, inputValue })
      if (isChecked) this.addAllItemsToSelected(itemsInGroup)
      else this.removeAllItemsFromSelected(itemsInGroup)
    },
    isGroupChecked({ groupId, inputValue }) {
      if (Array.isArray(inputValue)) return inputValue.includes(groupId)
      if (typeof inputValue === 'boolean') return inputValue
      return this.selectedGroups.includes(groupId)
    },
    getFullGroupItems({ groupId }) {
      const groupData = this.fullListItems.find(
        (entry) => this.getGroupId({ groupData: entry }) === groupId
      )
      return groupData && Array.isArray(groupData.items) ? groupData.items : []
    },
    addAllItemsToSelected(itemsInGroup) {
      const concatData = this.selectedItems.concat(
        itemsInGroup.map((i) => i.id)
      )
      this.selectedItems = Array.from(new Set(concatData))
    },
    removeAllItemsFromSelected(itemsInGroup) {
      const toRemove = new Set(itemsInGroup.map((i) => i.id))
      this.selectedItems = this.selectedItems.filter((id) => !toRemove.has(id))
    },
    syncSelectedGroups() {
      const selectedSet = new Set(this.selectedItems.map(String))
      const nextGroups = this.fullListItems
        .filter((groupData) =>
          this.isGroupFullySelected({ groupData, selectedSet })
        )
        .map((groupData) => this.getGroupId({ groupData }))
      this.selectedGroups = nextGroups
    },
    isGroupFullySelected({ groupData, selectedSet }) {
      const itemsList = Array.isArray(groupData.items) ? groupData.items : []
      const idsList = itemsList
        .map((it) =>
          it && it.id !== undefined && it.id !== null ? it.id : null
        )
        .filter((id) => id !== null)
        .map(String)
      if (idsList.length === 0) return false
      return idsList.every((id) => selectedSet.has(id))
    },
    isGroupIndeterminate(groupId) {
      const itemsInGroup = this.getFullGroupItems({ groupId })
      const ids = itemsInGroup
        .map((item) =>
          item && item.id !== undefined && item.id !== null
            ? String(item.id)
            : null
        )
        .filter((id) => id !== null)
      if (ids.length === 0) return false
      const selectedSet = new Set(this.selectedItems.map(String))
      const selectedCount = ids.filter((id) => selectedSet.has(id)).length
      return selectedCount > 0 && selectedCount < ids.length
    },
    pruneSelectedItems() {
      const availableIds = this.collectAvailableItemIds()
      this.selectedItems = this.selectedItems.filter((id) =>
        availableIds.has(String(id))
      )
    },
    collectAvailableItemIds() {
      const availableIds = new Set()
      const groupsList = this.fullListItems
      for (const groupData of groupsList) {
        const itemsList = Array.isArray(groupData.items) ? groupData.items : []
        let hasRealItem = false
        for (const entry of itemsList) {
          const entryId = entry ? entry.id : null
          if (entryId !== null && entryId !== undefined) {
            availableIds.add(String(entryId))
            hasRealItem = true
          }
        }
        if (this.selectableLeafGroups && !hasRealItem) {
          availableIds.add(String(this.getGroupId({ groupData })))
        }
      }
      return availableIds
    },
    syncOpenedGroups() {
      const groupIds = this.collectAvailableGroupIds()
      this.openedGroups = this.openedGroups.filter((id) => groupIds.has(id))
    },
    collectAvailableGroupIds() {
      const groupIds = new Set()
      const groupsList = this.fullListItems
      for (const groupData of groupsList) {
        groupIds.add(this.getGroupId({ groupData }))
      }
      return groupIds
    },
    getGroupItemClasses(groupId) {
      const opened = this.openedGroups.includes(groupId)
      return { 'transform-rotate-0': !opened, 'transform-rotate-180': opened }
    },
    isLeafSelectable(group) {
      return this.selectableLeafGroups && !group.hasItem
    },
    handleGroupLabelClick(group) {
      if (!group.hasItem || this.disabled) return
      this.toggleGroupSelection({
        groupId: group.id,
        inputValue: !this.selectedGroups.includes(group.id),
      })
    },
    isItemDisabled(id) {
      return this.disabled || this.disabledItems.includes(id)
    },
    openCloseTeam(groupId) {
      const index = this.openedGroups.indexOf(groupId)
      if (index > -1) this.openedGroups.splice(index, 1)
      else this.openedGroups.push(groupId)
    },
    groupItems(data) {
      const grouped = {}
      data.forEach(({ key, value }) => {
        if (!key) return
        if (!grouped[key]) grouped[key] = []
        if (value && typeof value === 'object') {
          Object.entries(value).forEach(([id, val]) => {
            grouped[key].push({ id, value: String(val) })
          })
        }
      })
      Object.keys(grouped).forEach((key) => {
        if (grouped[key].length === 0) grouped[key].push({})
      })
      return grouped
    },
    filterItems(data, search) {
      const searchText = this.normalizeSearchText({ searchText: search })
      return data
        .map((item) => this.filterGroupBySearch({ item, searchText }))
        .filter(Boolean)
    },
    normalizeSearchText({ searchText }) {
      return String(searchText || '')
        .trim()
        .toLowerCase()
    },
    filterGroupBySearch({ item, searchText }) {
      const groupKey = item && item.key ? item.key : ''
      if (this.groupMatchesSearch({ groupKey, searchText })) return item
      const filteredValue = this.filterObjectValuesBySearch({
        sourceObject: item ? item.value : null,
        searchText,
      })
      if (Object.keys(filteredValue).length === 0) return null
      return { key: item.key, value: filteredValue }
    },
    groupMatchesSearch({ groupKey, searchText }) {
      return this.textMatchesSearch({ textValue: groupKey, searchText })
    },
    filterObjectValuesBySearch({ sourceObject, searchText }) {
      const result = {}
      if (!sourceObject || typeof sourceObject !== 'object') return result
      Object.entries(sourceObject).forEach(([key, value]) => {
        if (this.textMatchesSearch({ textValue: value, searchText }))
          result[key] = value
      })
      return result
    },
    textMatchesSearch({ textValue, searchText }) {
      return String(textValue || '')
        .toLowerCase()
        .includes(searchText)
    },
    convertObjectToArray(obj) {
      return Object.entries(obj).map(([key, value]) => ({ key, value }))
    },
    setSelectedItems() {
      const selectedData = this.selectedItems
        .map((id) => this.findSelectedItem({ itemId: id }))
        .filter(Boolean)
      this.$emit('updateSelected', selectedData)
      this.$emit('input', this.selectedItems.slice())
    },
    findSelectedItem({ itemId }) {
      const source = this.items
      if (!source || typeof source !== 'object' || Array.isArray(source))
        return null
      const entries = Object.entries(source)
      for (const entry of entries) {
        const groupKey = entry ? entry[0] : ''
        const clientData = entry ? entry[1] : null
        if (
          this.selectableLeafGroups &&
          String(groupKey) === String(itemId) &&
          this.isLeafGroupValue(clientData)
        )
          return { id: itemId, value: groupKey }
        if (!clientData || typeof clientData !== 'object') continue
        const hasKey = Object.prototype.hasOwnProperty.call(clientData, itemId)
        if (hasKey) return { id: itemId, value: clientData[itemId] }
      }
      return null
    },
    getSelectedValuesById(items, ids) {
      const source = items
      if (!source || typeof source !== 'object' || Array.isArray(source))
        return []
      return ids
        .map((id) => this.findSelectedValue({ source, itemId: id }))
        .filter(Boolean)
    },
    findSelectedValue({ source, itemId }) {
      const entries = Object.entries(source)
      for (const entry of entries) {
        const groupKey = entry ? entry[0] : ''
        const clientData = entry ? entry[1] : null
        if (
          this.selectableLeafGroups &&
          String(groupKey) === String(itemId) &&
          this.isLeafGroupValue(clientData)
        )
          return groupKey
        if (!clientData || typeof clientData !== 'object') continue
        const hasKey = Object.prototype.hasOwnProperty.call(clientData, itemId)
        if (hasKey) return clientData[itemId]
      }
      return null
    },
    clearSelectedItems() {
      this.selectedGroups = []
      this.selectedItems = []
      if (this.hasBeenBlurred && this.$refs.input) this.$refs.input.validate()
    },
  },
}
</script>

<template>
  <div class="grouped-search" :style="{ maxWidth: normalizedMaxWidth }">
    <v-input
      ref="input"
      :value="validationValue"
      :rules="rules"
      :error-messages="errorMessages"
      :hide-details="!showDetails"
      :disabled="disabled"
      :loading="loading"
    >
      <v-menu
        v-model="show"
        :close-on-content-click="false"
        transition="scroll-y-transition"
        :max-width="normalizedMenuMaxWidth"
        :min-width="computedMenuMinWidth"
        min-height="auto"
        offset-y
        content-class="grouped-search__menu  rounded-xs"
        :disabled="disabled"
      >
        <template v-slot:activator="{ on, attrs }">
          <slot name="activator" :on="on" :attrs="attrs">
            <div class="grouped-search__select">
              <span
                v-if="label"
                class="text-sm"
                :class="{ 'grouped-search__label--disabled': disabled }"
              >
                {{ label }}
              </span>
              <div
                ref="activator"
                class="grouped-search__input-container rounded-xs"
                :class="{
                  'grouped-search__input-container--active': show,
                  'grouped-search__input-container--disabled': disabled,
                }"
                v-bind="attrs"
                v-on="on"
              >
                <SelectedItemsDisplay
                  :selectedItemsValues="selectedItemsValues"
                  :default-text="selectionPlaceholder"
                />
                <v-icon
                  class="grouped-search__icon"
                  :class="{ 'grouped-search__icon--active': show }"
                >
                  mdi-chevron-down
                </v-icon>
              </div>
            </div>
          </slot>
        </template>

        <div class="grouped-search__modal bg-white">
          <v-text-field
            ref="searchField"
            v-model="search"
            :placeholder="placeholder"
            class="new-ux"
            outlined
            autofocus
            :disabled="isSearchDisabled"
          />

          <div
            v-if="showClearSelectedItems"
            class="text-primary text-xs text-underline text-bold text-right hover"
            @click="clearSelectedItems"
          >
            Limpar seleção
          </div>

          <div v-if="loading" class="full-center pt-5">
            <v-progress-circular color="primary" indeterminate size="40" />
          </div>

          <div v-else class="grouped-search__group-items">
            <div v-for="group in groupList" :key="group.id">
              <div
                class="grouped-search__group-item bg-gray-2 rounded-xs"
                :class="{
                  'grouped-search__group-item--disabled':
                    !group.hasItem && !selectableLeafGroups,
                }"
              >
                <v-checkbox
                  v-if="isLeafSelectable(group)"
                  v-model="selectedItems"
                  :disabled="isItemDisabled(group.id)"
                  :value="group.id"
                  :label="group.value"
                  multiple
                  hide-details
                  color="primary"
                  class="m-0 p-0"
                />
                <div
                  v-else
                  class="grouped-search__group-toggle"
                  :class="{
                    'grouped-search__group-toggle--disabled':
                      !group.hasItem || disabled,
                  }"
                  @click="handleGroupLabelClick(group)"
                >
                  <v-simple-checkbox
                    :value="selectedGroups.includes(group.id)"
                    :indeterminate="isGroupIndeterminate(group.id)"
                    :disabled="!group.hasItem || disabled"
                    :ripple="false"
                    color="primary"
                    class="m-0 p-0"
                    @input="
                      toggleGroupSelection({
                        groupId: group.id,
                        inputValue: $event,
                      })
                    "
                  />
                  <span class="grouped-search__group-label">
                    {{ group.value }}
                  </span>
                </div>
                <v-btn
                  v-if="!isLeafSelectable(group)"
                  :disabled="!group.hasItem || disabled"
                  icon
                  small
                  @click="openCloseTeam(group.id)"
                >
                  <v-icon :class="getGroupItemClasses(group.id)">
                    mdi-chevron-down
                  </v-icon>
                </v-btn>
              </div>

              <div
                v-if="openedGroups.includes(group.id)"
                class="grouped-search__list-items pl-8"
              >
                <div
                  v-for="(item, index) in group.items"
                  :key="item && item.id ? item.id : index"
                >
                  <v-checkbox
                    v-model="selectedItems"
                    :disabled="isItemDisabled(item.id)"
                    :value="item.id"
                    :label="String(item[keyItem])"
                    multiple
                    hide-details
                    color="primary"
                    class="m-0 p-0"
                  />
                </div>
              </div>
            </div>

            <div v-if="isEmptyList" class="text-sm text-center text-color-900">
              Nenhum item encontrado.
            </div>
          </div>

          <div v-if="hasFooter" class="grouped-search__footer">
            <slot name="footer" />
          </div>
        </div>
      </v-menu>
    </v-input>
  </div>
</template>

<style lang="sass">
$grouped-search-border: 1px solid rgba(0, 0, 0, 0.24)
$modal-spacing: 8px
$disabled-opacity: .4

@mixin searchBorderWithHover()
  border: $grouped-search-border

  &:hover
    border: 1px solid var(--dark)
    cursor: pointer

@mixin selectSearchLabel()
  color: #8e8e8e !important
  font-weight: 600 !important
  font-size: 14px !important
  font-family: 'GeneralSans','Geist','OpenSans'

.grouped-search
  flex-grow: 1

  .v-input.v-input--has-state.error--text .grouped-search__input-container
    border-color: var(--error) !important

  .v-input.v-input--has-state.error--text .grouped-search__icon
    color: var(--error) !important

  &__menu
    margin-top: 6px
    border: $grouped-search-border

  &__select
    width: 100%
    display: flex
    flex-direction: column
    gap: 6px

  &__label
    &--disabled
      opacity: $disabled-opacity

  &__input-container
    display: flex
    justify-content: space-between
    align-items: center
    padding: 4px 8px
    height: var(--control-height)
    width: 100%
    min-width: 125px
    @include searchBorderWithHover()

    &--active
      border-color: var(--primary) !important

    &--disabled
      opacity: $disabled-opacity
      cursor: not-allowed
      pointer-events: none

  &__icon
    transition: transform 0.3s ease

    &--active
      transform: rotate(180deg)
      color: var(--primary) !important

  &__modal
    padding: 32px 10px
    width: 100%
    display: flex
    flex-direction: column
    gap: 8px

    .v-text-field__details
      display: none

    .v-input__slot
      margin-bottom: 0

  &__group-items
    max-height: 350px
    height: 50%
    overflow: auto
    display: flex
    flex-direction: column
    gap: 8px
    padding-bottom: 4px

  &__group-item
    display: flex
    justify-content: space-between
    align-items: center
    padding: 10px 15px
    margin-right: 4px

    .v-label
      @include selectSearchLabel()

    &--disabled
      opacity: .6

  &__group-toggle
    display: flex
    align-items: center
    gap: 8px
    cursor: pointer

    &--disabled
      opacity: $disabled-opacity
      cursor: not-allowed
      pointer-events: none

  &__group-label
    @include selectSearchLabel()

  &__list-items
    display: flex
    flex-direction: column
    gap: 10px
    padding: $modal-spacing

  &__footer
    display: flex
    justify-content: flex-end
    align-items: center
    padding-top: 8px
    margin-top: 4px
    border-top: $grouped-search-border
</style>
